/**
 * OmegaTopBar — 32 px top strip per spec §4.1.
 *
 * Layout, left to right:
 *   - OMEGA seal (Ω glyph in the accent colour)
 *   - environment tag (prod / stage / local) — read from VITE env
 *   - current time (UTC + local), tabular
 *   - founder identity badge — initial avatar + email
 *   - global kill-switch button (placeholder route to /omega/kill-switch)
 *
 * Phase 1: the kill-switch button is a deep-link, not a confirm
 * dialog. Per spec §8.2 the dashboard never bypasses the quorum;
 * the button only initiates a round inside the kill-switch page.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Power } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';

const TOP_BAR_HEIGHT = 32;

function detectEnv(): string {
  const apiBase =
    (import.meta.env.VITE_API_BASE_URL as string | undefined)?.toLowerCase() ?? '';
  if (apiBase.includes('localhost') || apiBase.includes('127.0.0.1')) return 'local';
  if (apiBase.includes('staging') || apiBase.includes('preview')) return 'stage';
  return 'prod';
}

export function OmegaTopBar() {
  const user = useAuthStore((s) => s.user);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const env = detectEnv();
  const utc = now.toISOString().slice(11, 19);
  const local = now.toLocaleTimeString(undefined, { hour12: false });

  const initial = (user?.email ?? '?').slice(0, 1).toUpperCase();

  return (
    <div
      style={{
        height: `${TOP_BAR_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--omega-gap-2)',
        padding: '0 var(--omega-gap-2)',
        background: 'var(--omega-surface)',
        borderBottom: '1px solid var(--omega-rim)',
        fontSize: '10pt',
        color: 'var(--omega-ink-muted)',
        flexShrink: 0,
      }}
    >
      {/* OMEGA seal */}
      <span
        style={{
          fontFamily: 'var(--omega-mono)',
          fontSize: '14pt',
          fontWeight: 700,
          color: 'var(--omega-accent)',
          letterSpacing: '0.04em',
        }}
        aria-label="OMEGA"
      >
        Ω
      </span>
      <span
        style={{
          fontFamily: 'var(--omega-sans)',
          fontWeight: 600,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--omega-ink)',
          fontSize: '10pt',
        }}
      >
        OMEGA
      </span>

      {/* env tag */}
      <span
        style={{
          fontFamily: 'var(--omega-mono)',
          fontSize: '9pt',
          letterSpacing: '0.08em',
          padding: '2px 6px',
          border: '1px solid var(--omega-rim)',
          borderRadius: 2,
          color:
            env === 'prod'
              ? 'var(--omega-state-healthy)'
              : env === 'stage'
                ? 'var(--omega-state-degraded)'
                : 'var(--omega-ink-subtle)',
          textTransform: 'uppercase',
        }}
      >
        {env}
      </span>

      {/* spacer */}
      <span style={{ flex: 1 }} />

      {/* time */}
      <span className="omega-mono" style={{ fontSize: '10pt' }}>
        {utc}{' '}
        <span style={{ color: 'var(--omega-ink-subtle)' }}>UTC</span>
        <span
          style={{
            color: 'var(--omega-ink-subtle)',
            margin: '0 6px',
          }}
        >
          ·
        </span>
        {local}
      </span>

      {/* founder identity */}
      {user ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: 'var(--omega-gap-2)',
            borderLeft: '1px solid var(--omega-rim)',
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: 'var(--omega-accent-soft)',
              color: 'var(--omega-accent)',
              fontWeight: 700,
              fontSize: '9pt',
              letterSpacing: 0,
            }}
          >
            {initial}
          </span>
          <span
            style={{
              fontFamily: 'var(--omega-mono)',
              fontSize: '9pt',
              color: 'var(--omega-ink-muted)',
            }}
          >
            {user.email}
          </span>
        </span>
      ) : null}

      {/* kill-switch */}
      <Link
        to="/omega/kill-switch"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          border: '1px solid var(--omega-state-critical)',
          color: 'var(--omega-state-critical)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontSize: '9pt',
          fontWeight: 700,
          borderRadius: 2,
        }}
        aria-label="Open kill switch page"
      >
        <Power size={11} strokeWidth={2.4} aria-hidden />
        Kill switch
      </Link>
    </div>
  );
}
